import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  directoryApi,
  parcelsApi,
  walletApi,
  notificationsApi,
  vehiclesApi,
  type CorporateAccount,
  type CorporateMember,
  type DriverDirectoryEntry,
  type Parcel,
  type Wallet,
  type WalletTransaction,
  type Notification,
  type Vehicle,
} from "@/lib/api";
import { formatNaira } from "@/lib/mock-data";
import { timeAgo, cn } from "@/lib/utils";
import { ArrowLeft, Building2, Truck, Package, Wallet as WalletIcon, Users, UserCog, Activity as ActivityIcon, Pencil, Ban, UserCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { useAuth } from "@/stores/auth";

const statusTone: Record<string, string> = {
  active: "bg-success/10 text-success border-success/20",
  trial: "bg-info/10 text-info border-info/20",
  churned: "bg-muted text-muted-foreground border-border",
};

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*";
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join("");
}

export default function CorporateDashboard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = useAuth((s) => s.session?.accessToken);
  const [corporate, setCorporate] = useState<CorporateAccount | null>(null);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [deliveries, setDeliveries] = useState<Parcel[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [activity, setActivity] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState<CorporateMember | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const [corp, allDrivers] = await Promise.all([
          directoryApi.corporate(id, token),
          directoryApi.drivers(token),
        ]);
        if (cancelled) return;
        setCorporate(corp);
        const myDrivers = allDrivers.filter((d) => d.corporate_account_id === id);
        setDrivers(myDrivers);

        const [allVehicles, deliveriesRes] = await Promise.all([
          vehiclesApi.list(token),
          parcelsApi.list({ clientId: corp.user_id ?? undefined, corporateAccountId: corp.id }, token),
        ]);
        if (cancelled) return;
        const driverIds = new Set(myDrivers.map((d) => d.id));
        setVehicles(allVehicles.filter((v) => v.current_driver_id && driverIds.has(v.current_driver_id)));
        setDeliveries(deliveriesRes);

        if (corp.user_id) {
          const [w, txns, feed] = await Promise.all([
            walletApi.get(corp.user_id, token).catch(() => null),
            walletApi.transactions(corp.user_id, token).catch(() => []),
            notificationsApi.list(corp.user_id, token).catch(() => []),
          ]);
          if (cancelled) return;
          setWallet(w);
          setTransactions(txns);
          setActivity(feed);
        }
      } catch {
        if (!cancelled) toast.error("Failed to load corporate dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const saveMember = async (patch: { name: string; email: string; role: string; password: string }) => {
    if (!editingMember || !id) return;
    try {
      const updated = await directoryApi.updateCorporateMember(id, editingMember.id, {
        name: patch.name,
        email: patch.email,
        role: editingMember.role === "owner" ? undefined : patch.role,
        password: patch.password || undefined,
      }, token);
      setCorporate(updated);
      setEditingMember(null);
      toast.success("Member updated");
    } catch {
      toast.error("Could not update member");
    }
  };

  const toggleMemberStatus = async (member: CorporateMember) => {
    if (!id) return;
    const blocking = member.user_status === "active";
    try {
      const updated = await directoryApi.setCorporateMemberStatus(id, member.id, blocking, token);
      setCorporate(updated);
      toast.success(blocking ? "Member deactivated" : "Member activated");
    } catch {
      toast.error(blocking ? "Could not deactivate member" : "Could not activate member");
    }
  };

  const resendActivation = async (member: CorporateMember) => {
    if (!id) return;
    try {
      await directoryApi.resendCorporateMemberActivation(id, member.id, token);
      toast.success("Activation email resent");
    } catch {
      toast.error("Could not resend activation email");
    }
  };

  if (loading) {
    return (
      <>
        <Topbar title="Corporate dashboard" />
        <div className="flex-1 p-4 md:p-6"><LoadingBlock label="Loading dashboard…" /></div>
      </>
    );
  }

  if (!corporate) {
    return (
      <>
        <Topbar title="Corporate dashboard" />
        <div className="flex-1 p-8 text-center text-sm text-muted-foreground">Corporate account not found.</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={corporate.company}
        subtitle="Corporate dashboard"
        actions={
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/users/corporates"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to corporates</Link>
          </Button>
        }
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="surface-card p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-soft text-primary">
              <Building2 className="h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{corporate.company}</h2>
                <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize", statusTone[corporate.status] ?? "bg-muted text-muted-foreground border-border")}>
                  {corporate.status}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{corporate.industry ?? "—"} · {corporate.contact_name ?? "no contact"} · {corporate.contact_email ?? "—"}</div>
            </div>
          </div>
          <dl className="mt-4 grid min-w-0 grid-cols-2 gap-2 rounded-lg bg-muted/40 p-3 text-center sm:grid-cols-5">
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Drivers</dt><dd className="mt-0.5 font-semibold">{drivers.length}</dd></div>
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Vehicles</dt><dd className="mt-0.5 font-semibold">{vehicles.length}</dd></div>
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Deliveries</dt><dd className="mt-0.5 font-semibold">{deliveries.length}</dd></div>
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Team</dt><dd className="mt-0.5 font-semibold">{corporate.members.length}</dd></div>
            <div className="min-w-0"><dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Wallet</dt><dd className="mt-0.5 truncate font-semibold">{wallet ? formatNaira(wallet.available_kobo / 100) : "—"}</dd></div>
          </dl>
        </div>

        <Tabs defaultValue="vehicles">
          <TabsList>
            <TabsTrigger value="vehicles"><Truck className="mr-1.5 h-3.5 w-3.5" /> Vehicles</TabsTrigger>
            <TabsTrigger value="deliveries"><Package className="mr-1.5 h-3.5 w-3.5" /> Deliveries</TabsTrigger>
            <TabsTrigger value="payments"><WalletIcon className="mr-1.5 h-3.5 w-3.5" /> Payments</TabsTrigger>
            <TabsTrigger value="drivers"><UserCog className="mr-1.5 h-3.5 w-3.5" /> Drivers</TabsTrigger>
            <TabsTrigger value="team"><Users className="mr-1.5 h-3.5 w-3.5" /> Team</TabsTrigger>
            <TabsTrigger value="activity"><ActivityIcon className="mr-1.5 h-3.5 w-3.5" /> Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="vehicles">
            <div className="surface-card">
              <Table>
                <TableHeader><TableRow><TableHead>Plate</TableHead><TableHead>Make/model</TableHead><TableHead>Status</TableHead><TableHead>Driver</TableHead></TableRow></TableHeader>
                <TableBody>
                  {vehicles.map((v) => {
                    const driver = drivers.find((d) => d.id === v.current_driver_id);
                    return (
                      <TableRow key={v.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/app/vehicles/${v.id}`)}>
                        <TableCell className="font-mono text-xs">{v.plate}</TableCell>
                        <TableCell>{[v.make, v.model].filter(Boolean).join(" ") || "—"}</TableCell>
                        <TableCell className="capitalize">{v.status}</TableCell>
                        <TableCell>{driver?.name ?? "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                  {vehicles.length === 0 ? <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">No vehicles assigned to this corporate's drivers.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="deliveries">
            <div className="surface-card">
              <Table>
                <TableHeader><TableRow><TableHead>Tracking ID</TableHead><TableHead>Status</TableHead><TableHead>Amount</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
                <TableBody>
                  {deliveries.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/app/tracking/${p.id}`)}>
                      <TableCell className="font-mono text-xs">{p.tracking_id}</TableCell>
                      <TableCell className="capitalize">{p.status.replace("_", " ")}</TableCell>
                      <TableCell>{formatNaira(p.amount_kobo / 100)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.created_at ? timeAgo(p.created_at) : "—"}</TableCell>
                    </TableRow>
                  ))}
                  {deliveries.length === 0 ? <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">No deliveries yet.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <div className="surface-card">
              {!corporate.user_id ? (
                <div className="p-6 text-center text-sm text-muted-foreground">No login account (no contact email) — no wallet.</div>
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {transactions.map((t) => (
                      <TableRow
                        key={t.id}
                        className={cn(t.parcel_id && "cursor-pointer hover:bg-muted/40")}
                        onClick={() => t.parcel_id && navigate(`/app/tracking/${t.parcel_id}`)}
                      >
                        <TableCell className="font-mono text-xs">{t.reference}</TableCell>
                        <TableCell className="capitalize">{t.type.replace("_", " ")}</TableCell>
                        <TableCell>{formatNaira(t.amount_kobo / 100)}</TableCell>
                        <TableCell className="capitalize">{t.status}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{timeAgo(t.occurred_at)}</TableCell>
                      </TableRow>
                    ))}
                    {transactions.length === 0 ? <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">No transactions yet.</TableCell></TableRow> : null}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="drivers">
            <div className="surface-card">
              <Table>
                <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Status</TableHead><TableHead>Vehicle</TableHead><TableHead>Active parcels</TableHead></TableRow></TableHeader>
                <TableBody>
                  {drivers.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{d.name}</TableCell>
                      <TableCell className="capitalize">{d.status}</TableCell>
                      <TableCell className="font-mono text-xs">{d.vehicle_plate ?? "—"}</TableCell>
                      <TableCell>{d.active_parcels}</TableCell>
                    </TableRow>
                  ))}
                  {drivers.length === 0 ? <TableRow><TableCell colSpan={4} className="py-6 text-center text-sm text-muted-foreground">No drivers in this fleet.</TableCell></TableRow> : null}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="team">
            <div className="surface-card divide-y">
              {corporate.members.map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                    {m.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{m.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{m.email}</div>
                  </div>
                  <span className="rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize">{m.role}</span>
                  {m.user_id && !m.activated ? (
                    <Button variant="ghost" size="icon" title="Resend activation email" onClick={() => resendActivation(m)}>
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  ) : null}
                  {m.user_id && m.activated ? (
                    m.user_status === "active" ? (
                      <Button variant="ghost" size="icon" title="Deactivate" onClick={() => toggleMemberStatus(m)}>
                        <Ban className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    ) : (
                      <Button variant="ghost" size="icon" title="Activate" onClick={() => toggleMemberStatus(m)}>
                        <UserCheck className="h-3.5 w-3.5 text-primary" />
                      </Button>
                    )
                  ) : null}
                  <Button variant="ghost" size="icon" onClick={() => setEditingMember(m)}><Pencil className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              {corporate.members.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No team members yet.</div> : null}
            </div>
          </TabsContent>

          <TabsContent value="activity">
            <div className="surface-card divide-y">
              {activity.map((n) => (
                <div key={n.id} className="p-3">
                  <div className="text-sm font-medium">{n.title}</div>
                  {n.detail ? <div className="text-xs text-muted-foreground">{n.detail}</div> : null}
                  <div className="mt-1 text-[10px] text-muted-foreground">{timeAgo(n.created_at)}</div>
                </div>
              ))}
              {activity.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">{corporate.user_id ? "No activity yet." : "No login account — no activity feed."}</div> : null}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <EditMemberDialog member={editingMember} onClose={() => setEditingMember(null)} onSave={saveMember} />
    </>
  );
}

function EditMemberDialog({
  member,
  onClose,
  onSave,
}: {
  member: CorporateMember | null;
  onClose: () => void;
  onSave: (patch: { name: string; email: string; role: string; password: string }) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (member) {
      setName(member.name);
      setEmail(member.email);
      setRole(member.role);
      setPassword("");
    }
  }, [member]);

  const isOwner = member?.role === "owner";

  return (
    <Dialog open={!!member} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit team member</DialogTitle>
          <DialogDescription>
            {member?.user_id ? "Has a login account — leave password blank to keep it unchanged." : "No login yet — set a password to activate one."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="edit-member-name">Name</Label>
            <Input id="edit-member-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="edit-member-email">Login email</Label>
            <Input id="edit-member-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole} disabled={isOwner}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {isOwner ? <SelectItem value="owner">Owner</SelectItem> : null}
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="member">Member</SelectItem>
              </SelectContent>
            </Select>
            {isOwner ? <p className="text-xs text-muted-foreground">Ownership can't be transferred here.</p> : null}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="edit-member-password">Password (optional)</Label>
            <div className="flex gap-2">
              <Input
                id="edit-member-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank to keep unchanged"
              />
              <Button type="button" variant="outline" onClick={() => setPassword(generatePassword())}>
                Generate
              </Button>
            </div>
            {password ? (
              <p className="text-xs text-muted-foreground">Share this with the member — it won't be shown again.</p>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (password && password.length < 12) {
                toast.error("Password must be at least 12 characters");
                return;
              }
              onSave({ name, email, role, password });
            }}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
